import { AfterViewInit, Component, DestroyRef, ElementRef, NgZone, computed, effect, inject, signal, viewChild } from '@angular/core';

@Component({
  selector: 'app-pb-news',
  standalone: true,
  imports: [],
  templateUrl: './pb-news.component.html',
  styleUrl: './pb-news.component.scss'
})
export class PbNewsComponent implements AfterViewInit {

  //-------------------------- Constants ---------------------------------

  /** OnFirstup embed page mounted into the iframe */
  protected readonly ctEmbedUrl = 'https://app.onfirstup.com/embed/91330afb-5a9c-426a-a52f-2e84f8df883e';

  /** Longest the mount waits for an idle window (ms) */
  protected readonly ctIdleTimeoutMs = 2000;

  /** Placeholder row height plus gap (px) */
  protected readonly ctSkeletonRowPitch = 100;

  //--------------------- Component State --------------------------------

  /** Host element the embed iframe is mounted into */
  protected embedHost = viewChild<ElementRef<HTMLElement>>('embedHost');

  /** Measured height available for the embed (px) */
  protected embedHeight = signal<number>(0);

  /** True once the embed document has finished loading */
  protected embedLoaded = signal<boolean>(false);

  /** The mounted iframe — tracked as a signal so height sync re-runs on mount */
  protected embedFrame = signal<HTMLIFrameElement | null>(null);

  /** Enough placeholder rows to fill the measured frame height */
  protected skeletonRows = computed<number[]>(() => {

    const count = Math.max(1, Math.ceil(this.embedHeight() / this.ctSkeletonRowPitch));

    return Array.from({ length: count }, (_, index) => index);
  });

  protected destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);

  //------------------------ Lifecycle Methods ----------------------------

  constructor() {

    this.setupHostObserver();
    this.syncEmbedHeight();
  }

  ngAfterViewInit(): void {

    this.scheduleEmbed();
  }

  //-------------------------- Embed --------------------------------------

  /** Defers the mount until the browser is idle so the third-party load never
   *  competes with the app's own initial requests. */
  protected scheduleEmbed(): void {

    const idle = window.requestIdleCallback;

    if (idle == null) {

      const timer = setTimeout(() => this.loadEmbed(), 0);
      this.destroyRef.onDestroy(() => clearTimeout(timer));

      return;
    }

    const handle = idle(() => this.zone.run(() => this.loadEmbed()), { timeout: this.ctIdleTimeoutMs });
    this.destroyRef.onDestroy(() => window.cancelIdleCallback?.(handle));
  }

  /** Builds the embed iframe directly, mirroring the vendor loader */
  protected loadEmbed(): void {

    const host = this.embedHost()?.nativeElement;

    if (host == null) {

      return;
    }

    const height = this.embedHeight();

    const iframe = document.createElement('iframe');
    iframe.src = this.ctEmbedUrl;
    iframe.title = 'Latest news and publications';
    iframe.loading = 'lazy';
    iframe.style.width = '100%';
    iframe.style.height = height > 0 ? `${height}px` : '100%';
    iframe.style.border = 'none';
    iframe.style.display = 'block';
    iframe.addEventListener('load', () => this.embedLoaded.set(true), { once: true });

    host.replaceChildren(iframe);
    this.embedFrame.set(iframe);

    this.destroyRef.onDestroy(() => host.replaceChildren());
  }

  /** Applies the measured height to the mounted iframe */
  protected syncEmbedHeight(): void {

    effect(() => {

      const iframe = this.embedFrame();
      const height = this.embedHeight();

      if (iframe == null || height <= 0) {

        return;
      }

      iframe.style.height = `${height}px`;
    });
  }

  /** Tracks the host's resolved height so the iframe fills the frame */
  protected setupHostObserver(): void {

    effect((onCleanup) => {

      const host = this.embedHost()?.nativeElement;

      if (host == null) {

        return;
      }

      const observer = new ResizeObserver(entries => {

        const box = entries[0]?.contentRect;

        if (box == null) {

          return;
        }

        this.embedHeight.set(box.height);
      });

      observer.observe(host);
      onCleanup(() => observer.disconnect());
    });
  }

}
